import PQueue from "p-queue";
import type { PreviewBounds } from "../api/destinationDialog";

/** What a preview fetch resolved to: the image URL, and — when the endpoint
 *  reported it — the page-normalized frame those pixels actually cover. The
 *  two are cached together, so a crop that resolves straight from the cache
 *  still answers the second question. */
export interface PreviewResult {
    url: string;
    bounds?: PreviewBounds;
}

type Fetcher = (signal: AbortSignal) => Promise<string | PreviewResult>;

export function immutablePreviewFetchInit(signal?: AbortSignal): RequestInit {
    return { signal, cache: "force-cache" };
}

/** Revalidate on every use: the 304 still saves the bytes, but a URL two projects share (cloned
 *  tenants share file UUIDs) never serves the other project's cached body. */
export function revalidatedPreviewFetchInit(signal?: AbortSignal): RequestInit {
    return { signal, cache: "no-cache" };
}

/** Never read or write the HTTP cache: for a resource whose bytes behind one URL can change. */
export function uncachedPreviewFetchInit(signal?: AbortSignal): RequestInit {
    return { signal, cache: "no-store" };
}

/**
 * Handles preview image API response - extracts URL from JSON or creates blob URL.
 * Use this to avoid duplicating response handling logic across preview components.
 *
 * `bounds` is the frame the endpoint says it rendered, which for a small crop is
 * not the frame that was requested — it rounds to hundredths and widens any axis
 * under its minimum span. Absent on an image response and on an older engine.
 */
export async function handlePreviewResponse(res: Response): Promise<PreviewResult> {
    if (!res.ok) {
        throw new Error(`Preview request failed: ${res.status} ${res.statusText}`);
    }

    const ct = res.headers.get("content-type") || "";
    if (ct.includes("application/json")) {
        const { url, bounds } = await res.json();
        return { url, bounds }; // direct SAS url
    } else {
        const blob = await res.blob();
        return { url: URL.createObjectURL(blob) };
    }
}

// Detects network errors from fetch by error type and name.
export const isNetworkFetchError = (error: unknown): boolean => {
    // TypeError is thrown for network errors in fetch in most browsers
    if (error instanceof TypeError) {
        return true;
    }
    // DOMException with name "NetworkError" is also a network error
    if (error instanceof DOMException && error.name === "NetworkError") {
        return true;
    }
    return false;
};

/**
 * Which queue a preview waits in. Every size but "hd" is cut from the page bitmap the server
 * caches, and shares the default lane (four at once). An "hd" crop is rendered from the PDF, one
 * at a time per server process with at most two waiting and the oldest turned away with a 503
 * (`app/file_preview_image.py`, `_NewestFirstSlot`), so it has a lane of its own: a floor that
 * asks for a dozen at once keeps one rendering and one waiting, and never queues behind the
 * cheap crops or crowds them out.
 */
export type PreviewLane = "default" | "hd";

/** How many hd crops this page asks the server for at once: one rendering, one next. */
export const HD_LANE_CONCURRENCY = 2;

/** The lane a crop URL rides: "hd" when it asks for `size=hd`. */
export function laneForUrl(url: string): PreviewLane {
    return /[?&]size=hd(&|$)/.test(url) ? "hd" : "default";
}

class PreviewManager {
    /** Each lane serves its newest request first (LIFO): the crop the reader asked for last. */
    private queues: Record<PreviewLane, PQueue> = {
        default: new PQueue({ concurrency: 4 }),
        hd: new PQueue({ concurrency: HD_LANE_CONCURRENCY }),
    };
    private pri = 0; // for LIFO
    /** One request per key; `waiters` counts the callers sharing it that have not cancelled. */
    private inflight = new Map<
        string,
        { promise: Promise<string>; controller: AbortController; waiters: number }
    >();

    // NB: Map remains insertion order so we use it for a simple LRU cache: a hit moves its
    // entry to the end, and the first entry is the one evicted. An evicted or replaced blob URL
    // is revoked, so the entry a surface still shows should be the last to go.
    private cache = new Map<string, string>();
    // Served frames, keyed and evicted in lockstep with `cache` so the two can
    // never disagree about which pixels a URL is.
    private servedBounds = new Map<string, PreviewBounds>();
    private cacheLimit = 200;

    // This is a module-global singleton, so it outlives the per-org QueryClient
    // (and any component tree) that gets torn down and remounted on an
    // organization switch. Every cache/inflight key is prefixed with the active
    // org so a matching key from a different tenant can never resolve to a
    // previously cached crop without a fresh, authenticated request.
    private activeOrgSlug: string | null = null;

    /** Scopes every subsequent key to this tenant. Call on every organization switch. */
    setActiveOrganization(orgSlug: string | null): void {
        this.activeOrgSlug = orgSlug?.trim() || null;
    }

    /**
     * The tenant keys are being scoped to right now.
     *
     * `get()` computes the scoped key *before* awaiting its fetcher, so a caller that
     * fetches through it is bound to the org the request started under whatever happens
     * mid-flight. A caller that fetched elsewhere and primes an already-resolved url
     * has no such binding — it must read this before its request and check it again
     * before priming, or its result is filed under whichever tenant happens to be
     * active when it lands.
     */
    get activeOrganization(): string | null {
        return this.activeOrgSlug;
    }

    private scopedKey(key: string): string {
        return `${this.activeOrgSlug ?? "no-org"}:${key}`;
    }

    private revokeIfBlob(url: string) {
        if (url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
        }
    }

    private setCache(key: string, result: PreviewResult) {
        // If replacing an existing entry, revoke the old blob URL
        const existing = this.cache.get(key);
        if (existing) {
            this.revokeIfBlob(existing);
            this.cache.delete(key);
        }
        this.cache.set(key, result.url);
        if (result.bounds) this.servedBounds.set(key, result.bounds);
        else this.servedBounds.delete(key);
        // Evict oldest entry if over limit, revoking blob URL to prevent leak
        if (this.cache.size > this.cacheLimit) {
            const firstKey = this.cache.keys().next().value as string;
            const firstVal = this.cache.get(firstKey);
            if (firstVal) this.revokeIfBlob(firstVal);
            this.cache.delete(firstKey);
            this.servedBounds.delete(firstKey);
        }
    }

    /** The frame the pixels cached under `key` cover, when the endpoint reported
     *  it. Read it after `get` resolves: a crop already in the cache never runs
     *  its fetcher, so this is the only way that caller learns the frame. */
    boundsFor(key: string): PreviewBounds | null {
        return this.servedBounds.get(this.scopedKey(key)) ?? null;
    }

    /**
     * Runs `task` through `lane`'s queue, newest first, without caching its result: for a caller
     * that wants the bytes of a sharp crop (the Drawings 3D view's ink), not a blob URL. An hd crop
     * fetched this way still waits its turn with every other hd crop, so two surfaces never crowd
     * the server's one render slot between them.
     */
    inLane<T>(lane: PreviewLane, task: () => Promise<T>): Promise<T> {
        return this.queues[lane].add(task, { priority: ++this.pri }) as Promise<T>;
    }

    /** Whether `key` is already cached, so a caller can tell whether its `get` would add an entry. */
    has(key: string): boolean {
        return this.cache.has(this.scopedKey(key));
    }

    /** The crop under `key`: from the cache, joined to the same request in flight, or queued
     *  newest first in `lane`. */
    get(key: string, fetcher: Fetcher, lane: PreviewLane = "default"): Promise<string> {
        const scopedKey = this.scopedKey(key);

        // 1) Memory cache, refreshed as the most recently used
        const cached = this.cache.get(scopedKey);
        if (cached) {
            this.cache.delete(scopedKey);
            this.cache.set(scopedKey, cached);
            return Promise.resolve(cached);
        }

        // 2) De-dupe in-flight per key but avoid reusing aborted/stale entries
        const existing = this.inflight.get(scopedKey);
        if (existing && !existing.controller.signal.aborted) {
            existing.waiters += 1;
            return existing.promise;
        }

        // 3) Enqueue with LIFO priority
        const controller = new AbortController();

        // Make the task explicitly return string to keep types happy
        const task: () => Promise<string> = async () => {
            try {
                const fetched = await fetcher(controller.signal);
                const result = typeof fetched === "string" ? { url: fetched } : fetched;
                if (controller.signal.aborted) {
                    this.revokeIfBlob(result.url);
                    return "";
                }
                this.setCache(scopedKey, result);
                return result.url;
            } catch (error: any) {
                // Cancelled requests are not errors - return empty string
                if (error?.name === "AbortError") {
                    return "";
                }

                if (isNetworkFetchError(error)) {
                    return "";
                }
                // Re-throw real errors
                throw error;
            }
        };

        const promise: Promise<string> = this.queues[lane]
            .add(task, { priority: ++this.pri })
            .then((result) => result || "");

        // Track inflight, but don't alter the returned promise's type
        this.inflight.set(scopedKey, { promise, controller, waiters: 1 });
        // Only delete if we're still the active promise for this key
        const settle = () => {
            const current = this.inflight.get(scopedKey);
            if (current?.promise === promise) {
                this.inflight.delete(scopedKey);
            }
        };
        // An ignored finally() promise rethrows a failed preview globally even
        // when the image consumer handles the original rejection. Settle both
        // branches without creating a second, unobserved rejecting promise.
        void promise.then(settle, settle);

        return promise;
    }

    /**
     * One caller no longer needs `key`. The shared request is aborted (and resolves to "") only
     * once every caller that joined it has cancelled: a prefetch or an unmounting image must not
     * blank another surface still waiting on the same picture.
     */
    cancel(key: string) {
        const scopedKey = this.scopedKey(key);
        const item = this.inflight.get(scopedKey);
        if (item && --item.waiters <= 0) {
            // Abort the associated fetch (if/when it runs) and drop the inflight entry
            item.controller.abort(new DOMException("Preview request cancelled", "AbortError"));
            this.inflight.delete(scopedKey);
        }
    }

    /** Remove a cached entry and revoke blob URL if applicable. */
    delete(key: string) {
        const scopedKey = this.scopedKey(key);
        const url = this.cache.get(scopedKey);
        if (url) this.revokeIfBlob(url);
        this.cache.delete(scopedKey);
        this.servedBounds.delete(scopedKey);
    }

    dropPending() {
        for (const queue of Object.values(this.queues)) queue.clear(); // doesn't cancel running tasks
    }

    clear() {
        for (const queue of Object.values(this.queues)) queue.clear();
        for (const item of this.inflight.values()) item.controller.abort();
        this.inflight.clear();
        for (const value of this.cache.values()) this.revokeIfBlob(value);
        this.cache.clear();
        this.servedBounds.clear();
    }
}

export const previewManager = new PreviewManager();
